import { diffArrays, diffChars } from 'diff';
import { buildDocumentVersionDiff, normalizeCanonicalMarkdown } from './documentVersionDiff.js';

export const DOCUMENT_PREVIEW_STAGES = Object.freeze({
  preparing: 'Preparando el documento', drafting: 'Redactando', editing: 'Aplicando tus cambios',
  checking: 'Comprobando el documento', saving: 'Guardando', stopping: 'Deteniendo…',
  incomplete: 'Borrador incompleto · No guardado', cancelled: 'Borrador incompleto · No guardado',
  failed: 'Borrador incompleto · No guardado', superseded: 'Borrador incompleto · No guardado',
});

// References are incomplete until their closing marker arrives. They never become
// document text, including when an update splits a private marker across batches.
export function readablePreviewText(value) {
  let source = String(value || '');
  const sentinels = ['<<<LEXIUS_REFERENCE_MAP_START>>>', '<<<LEXIUS_REFERENCE_MAP_END>>>'];
  source = source.replace(/<<<LEXIUS_REFERENCE_MAP_START>>>[\s\S]*?(?:<<<LEXIUS_REFERENCE_MAP_END>>>|$)/g, '')
    .replace(/<<<LEXIUS_REFERENCE_MAP_END>>>/g, '');
  // Hold any unfinished sentinel suffix until the next batch identifies it.
  for (const sentinel of sentinels) {
    for (let size = sentinel.length - 1; size >= 3; size--) {
      if (source.endsWith(sentinel.slice(0, size))) { source = source.slice(0, -size); break; }
    }
  }
  return source
    .replace(/^[\t ]*\[(?:R|D)\s*-?\s*0*\d+\]\s*:\s*[^\n]*(?:\r?\n|$)/gim, '')
    .replace(/\[(?:R|D)\s*-?\s*0*\d+(?:\s*[,;]\s*(?:R|D)\s*-?\s*0*\d+)*\](?!\s*\()/gi, '')
    .replace(/\[(?:R|D)[\s\d,;RD-]*$/gi, '')
    .replace(/\[\[(?:LEXIUS|SOURCE|REF|CITE)[\s\S]*?(?:\]\]|$)/gi, '')
    .replace(/\[(?:\[(?:L(?:E(?:X(?:I(?:U(?:S)?)?)?)?)?|S(?:O(?:U(?:R(?:C(?:E)?)?)?)?)?|R(?:E(?:F)?)?|C(?:I(?:T(?:E)?)?)?)?)?$/gi, '')
    .replace(/[^]*(?:|$)/g, '')
    .replace(/\[([^\]\n]*)\]\([^\)\n]*$/g, '$1');
}

export function previewMarkdown(value) {
  const source = readablePreviewText(value);
  const renderer = typeof window !== 'undefined' ? window.marked : null;
  if (typeof renderer?.lexer === 'function') {
    const tokens = renderer.lexer(source);
    const paragraphs = [];
    for (const token of tokens) {
      if (token.type === 'space' && paragraphs.length) paragraphs[paragraphs.length - 1] += token.raw;
      else if (token.raw) paragraphs.push(token.raw);
    }
    const referenceLinks = tokens.links || {};
    return { paragraphs, referenceLinks, referenceKey: JSON.stringify(referenceLinks) };
  }
  // Plaintext fallback when the optional Markdown renderer is unavailable.
  const parts = []; let block = ''; let fence = '';
  for (const line of source.split(/(?<=\n)/)) {
    const match = line.match(/^[\t ]{0,3}(`{3,}|~{3,})(.*)/);
    if (match) {
      if (!fence) fence = match[1];
      else if (match[1][0] === fence[0] && match[1].length >= fence.length && !match[2].trim()) fence = '';
    }
    block += line;
    if (!fence && /^\s*\n$/.test(line)) { parts.push(block); block = ''; }
  }
  if (block) parts.push(block);
  return { paragraphs: parts, referenceLinks: {}, referenceKey: '{}' };
}

export function previewParagraphs(value) {
  return previewMarkdown(value).paragraphs;
}

export function previewChanges(base, candidate) {
  const baseDocument = previewMarkdown(normalizeCanonicalMarkdown(base));
  const candidateDocument = previewMarkdown(normalizeCanonicalMarkdown(candidate));
  const before = baseDocument.paragraphs; const after = candidateDocument.paragraphs;
  const difference = diffArrays(before, after, { timeout: 40, maxEditLength: 1000 });
  const chunks = difference || [{ removed: true, value: before }, { added: true, value: after }];
  return chunks.flatMap(part => {
    const document = part.removed ? baseDocument : candidateDocument;
    return part.value.map(text => ({ text, kind: part.added ? 'added' : part.removed ? 'removed' : 'equal',
      referenceLinks: document.referenceLinks, referenceKey: document.referenceKey }));
  });
}

export function reconcilePreviewBlocks(previous, next, allocate) {
  const changes = diffArrays(previous, next, {
    comparator: (before, after) => before.kind === after.kind && before.text === after.text,
    timeout: 40, maxEditLength: 1000,
  }) || [{ removed: true, value: previous }, { added: true, value: next }];
  const result = []; let beforeIndex = 0; let afterIndex = 0;
  let removed = []; let added = [];
  const flush = () => {
    // Follow the candidate through equal <-> added transitions. A newly
    // inserted removed block must not steal its paragraph's reading anchor.
    const priorCandidates = removed.filter(block => block.kind !== 'removed');
    const priorRemoved = removed.filter(block => block.kind === 'removed');
    const replacements = added.map(block => {
      const pool = block.kind === 'removed' ? priorRemoved : priorCandidates;
      return { block, prior: pool.shift() };
    });
    // Entirely deleted/reinstated paragraphs can cross the remaining boundary.
    for (const replacement of replacements) {
      replacement.prior ||= (replacement.block.kind === 'removed' ? priorCandidates : priorRemoved).shift();
      result.push({ ...replacement.block, key: replacement.prior?.key ?? allocate() });
    }
    removed = []; added = [];
  };
  for (const change of changes) {
    const count = change.value.length;
    if (change.removed) { removed.push(...previous.slice(beforeIndex, beforeIndex + count)); beforeIndex += count; }
    else if (change.added) { added.push(...next.slice(afterIndex, afterIndex + count)); afterIndex += count; }
    else {
      flush();
      for (let index = 0; index < count; index++) {
        const prior = previous[beforeIndex++]; const block = next[afterIndex++];
        result.push(block.referenceKey === undefined ? prior : { ...prior, referenceLinks: block.referenceLinks, referenceKey: block.referenceKey });
      }
    }
  }
  flush();
  return result;
}

// Keep paragraph identities for live reading, but present each adjacent
// replacement as the same Antes/Ahora change region used by saved documents.
export function previewComparisonSections(blocks, previous = []) {
  const sections = [];
  for (const block of blocks) {
    const last = sections.at(-1);
    if (block.kind !== 'equal' && last?.kind === 'change') last.blocks.push(block);
    else sections.push({ kind: block.kind === 'equal' ? 'equal' : 'change', blocks: [block] });
  }
  const cached = new Map(previous.filter(section => section.signature).map(section => [section.signature, section]));
  // All new regions share one refinement budget. Repeated notifications and
  // unchanged regions reuse their word comparison, even in the clean view.
  const deadline = Date.now() + 40;
  let index = 0;
  return sections.map(section => {
    const before = section.blocks.filter(block => block.kind === 'removed');
    const after = section.blocks.filter(block => block.kind !== 'removed');
    section.key = after[0]?.key || before[0]?.key;
    if (section.kind === 'equal') return section;
    section.index = index++;
    const beforeText = before.map(block => normalizeCanonicalMarkdown(block.text)).join('');
    const afterText = after.map(block => normalizeCanonicalMarkdown(block.text)).join('');
    section.signature = JSON.stringify([beforeText, afterText]);
    const model = cached.get(section.signature)?.model || buildDocumentVersionDiff(beforeText, afterText, {
      previousBlocks: beforeText ? [beforeText] : [], nextBlocks: afterText ? [afterText] : [],
      timeoutMs: Math.max(0, deadline - Date.now()), maxEditLength: 1000, maxRefinementChars: 12000,
    });
    section.model = model;
    section.label = before.length && after.length ? 'Texto modificado' : after.length ? 'Texto añadido' : 'Texto eliminado';
    const splitSegments = (paragraphs, side) => {
      const segments = model.hunks.flatMap(hunk => hunk[side]);
      let segmentIndex = 0; let offset = 0;
      return paragraphs.map(block => {
        const result = []; let remaining = normalizeCanonicalMarkdown(block.text).length;
        while (remaining > 0 && segmentIndex < segments.length) {
          const segment = segments[segmentIndex];
          const value = segment.value.slice(offset, offset + remaining);
          if (value) result.push({ type: segment.type, value });
          remaining -= value.length; offset += value.length;
          if (offset === segment.value.length) { segmentIndex++; offset = 0; }
        }
        return { ...block, segments: result };
      });
    };
    section.before = splitSegments(before, 'before');
    section.after = splitSegments(after, 'after');
    return section;
  });
}

// Map comparison regions to the document by position, so identical paragraphs
// keep distinct markers. Deleted passages link to the existing comparison.
export function previewDocumentSections(blocks, comparison = []) {
  const document = blocks.map(block => ({ key: block.key, kind: 'equal', blocks: [block] }));
  if (!comparison.length) return document;
  const sections = [];
  let offset = 0;
  for (const change of comparison) {
    if (change.kind === 'equal') {
      sections.push(...document.slice(offset, offset + change.blocks.length));
      offset += change.blocks.length;
      continue;
    }
    if (!change.after.length) {
      sections.push({ key: `deletion-${change.key}`, kind: 'deletion', blocks: [], change, marker: true, fresh: change.fresh });
    } else {
      change.after.forEach((block, index) => {
        if (document[offset]) sections.push({ ...document[offset], change, marker: index === 0, fresh: change.fresh });
        offset++;
      });
    }
  }
  return sections.concat(document.slice(offset));
}

export function mapPreviewTextOffset(before, after, offset, affinity = 'after') {
  if (before === after) return Math.min(offset, after.length);
  const changes = diffChars(before, after, { timeout: 20, maxEditLength: 2000 });
  if (!changes) return null;
  let oldOffset = 0; let newOffset = 0;
  for (const change of changes) {
    if (change.added) { newOffset += change.value.length; continue; }
    const end = oldOffset + change.value.length;
    if (offset < end || (offset === end && affinity === 'before')) {
      if (change.removed) return offset === oldOffset || offset === end ? newOffset : null;
      return newOffset + offset - oldOffset;
    }
    oldOffset = end;
    if (!change.removed) newOffset += change.value.length;
  }
  return newOffset;
}

export function previewOwnsReceipt(preview, receipt) {
  if (!preview || !receipt || receipt.tool_name !== 'crear_editar_archivo') return false;
  const operationRuns = [preview.tool_run_id, ...(Array.isArray(preview.prior_tool_run_ids) ? preview.prior_tool_run_ids : [])].filter(Boolean).map(String);
  if (operationRuns.includes(String(receipt.tool_run_id || ''))) return true;
  const committed = preview.committed;
  return Boolean(committed && committed.artifact_id && receipt.result_ref?.type === 'document'
    && String(committed.artifact_id) === String(receipt.result_ref.id)
    && Number(committed.version) === Number(receipt.ui_payload?.counts?.version));
}

export function advancePreviewHighlights(previous, regions, { revision, candidate, enabled = true }) {
  const signatures = new Set(regions.filter(region => region.kind !== 'equal').map(region => `${region.kind}\0${region.text}`));
  const sameCandidate = previous && previous.revision === revision && previous.candidate === candidate;
  const fresh = new Set();
  if (!sameCandidate && enabled) {
    for (const signature of signatures) if (!previous?.regions.has(signature)) fresh.add(signature);
  }
  // Only the current candidate is retained. Both views consume the same
  // highlight opportunity, so switching views never replays an arrival.
  return { revision, candidate, regions: signatures, fresh };
}
